/**
 * Utility functions for dynamic date-based conditional logic comparisons
 */

/**
 * Check if a date is older than the specified number of years
 * @param dateValue - The date value to check (YYYY-MM-DD format)
 * @param years - The number of years to compare against (as string)
 * @returns true if the date is older than the specified years
 */
export function isDateOlderThanYears(dateValue: string, years: string): boolean {
  if (!dateValue || !years) return false;

  try {
    const inputDate = new Date(`${dateValue}T00:00:00`);
    const currentDate = new Date();
    const yearsToSubtract = parseInt(years, 10);

    // Validate inputs
    if (Number.isNaN(inputDate.getTime()) || Number.isNaN(yearsToSubtract) || yearsToSubtract < 0) {
      return false;
    }

    // Calculate the date that is exactly 'years' ago from today
    const yearsAgoDate = new Date(
      currentDate.getFullYear() - yearsToSubtract,
      currentDate.getMonth(),
      currentDate.getDate(),
    );

    // Return true if input date is older than (less than or equal to) the years-ago date
    return inputDate <= yearsAgoDate;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch {
    // Handle any date parsing errors gracefully
    return false;
  }
}

/**
 * Check if a date is younger than the specified number of years
 * @param dateValue - The date value to check (YYYY-MM-DD format)
 * @param years - The number of years to compare against (as string)
 * @returns true if the date is younger than the specified years
 */
export function isDateYoungerThanYears(dateValue: string, years: string): boolean {
  if (!dateValue || !years) return false;

  try {
    const inputDate = new Date(`${dateValue}T00:00:00`);
    const currentDate = new Date();
    const yearsToSubtract = parseInt(years, 10);

    // Validate inputs
    if (Number.isNaN(inputDate.getTime()) || Number.isNaN(yearsToSubtract) || yearsToSubtract < 0) {
      return false;
    }

    // Calculate the date that is exactly 'years' ago from today
    const yearsAgoDate = new Date(
      currentDate.getFullYear() - yearsToSubtract,
      currentDate.getMonth(),
      currentDate.getDate(),
    );

    // Return true if input date is younger than (greater than) the years-ago date
    return inputDate > yearsAgoDate;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch {
    // Handle any date parsing errors gracefully
    return false;
  }
}

/**
 * Calculate the age in years from a birth date
 * @param birthDate - The birth date (YYYY-MM-DD format)
 * @returns The age in years, or null if invalid date
 */
export function calculateAgeInYears(birthDate: string): number | null {
  if (!birthDate) return null;

  try {
    const birth = new Date(birthDate);
    const today = new Date();

    if (Number.isNaN(birth.getTime())) return null;

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // Adjust if birthday hasn't occurred this year yet
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      // eslint-disable-next-line no-plusplus
      age--;
    }

    return age >= 0 ? age : null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch {
    return null;
  }
}

/**
 * Validate if a date string is in valid YYYY-MM-DD format
 * @param dateValue - The date value to validate
 * @returns true if valid date format
 */
export function isValidDateFormat(dateValue: string): boolean {
  if (!dateValue) return false;

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateValue)) return false;

  const date = new Date(dateValue);
  return (
    date instanceof Date &&
    !Number.isNaN(date.getTime()) &&
    dateValue === date.toISOString().split('T')[0]
  );
}
